import React from 'react';
import { Link } from 'react-router-dom';
import { FaEye } from 'react-icons/fa';
import { Plus } from 'lucide-react';
import Swal from 'sweetalert2';
import { routesMap } from '../../routes/AppRoutes';
import { Bank } from '../../api/apiTypes';

interface ContainerBanksPageProps {
  banks: Bank[];
  addBank: (name: string) => Promise<void>;
}

const ContainerBanksPage: React.FC<ContainerBanksPageProps> = ({ banks, addBank }) => {
  const EyeIcon = FaEye as React.ComponentType<React.SVGProps<SVGSVGElement>>;

  const handleAddBank = async () => {
    const { value: nomeBanca } = await Swal.fire({
      title: 'Aggiungi nuova Banca',
      input: 'text',
      inputLabel: 'Nome della banca',
      inputPlaceholder: 'Inserisci il nome della banca',
      showCancelButton: true,
      confirmButtonText: 'Aggiungi',
      cancelButtonText: 'Annulla',
      reverseButtons: true,
      inputValidator: (value) => {
        if (!value.trim()) return 'Il nome della banca è obbligatorio';
        return null;
      },
      customClass: {
        confirmButton: 'bg-[#1B365D] text-white hover:bg-[#142a4a] px-4 py-2 rounded ml-2',
        cancelButton:  'bg-gray-100 text-gray-600 hover:bg-gray-200 px-4 py-2 rounded',
        actions: 'flex justify-between gap-2',
      },
      buttonsStyling: false,
    });

    if (nomeBanca) {
      try {
        await addBank(nomeBanca.trim());
        Swal.fire({
          icon: 'success',
          title: 'Banca aggiunta',
          text: `Hai aggiunto la banca: ${nomeBanca}`,
          confirmButtonText: 'OK',
          customClass: { confirmButton: 'bg-[#1B365D] text-white px-4 py-2 rounded' },
          buttonsStyling: false,
        });
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Errore',
          text: 'Impossibile creare la banca. Riprova più tardi.',
          confirmButtonText: 'OK',
          customClass: { confirmButton: 'bg-red-600 text-white px-4 py-2 rounded' },
          buttonsStyling: false,
        });
      }
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={handleAddBank}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-[#1B365D] hover:bg-[#142a4a] transition-colors"
        >
          Aggiungi Banca
          <Plus className="w-4 h-4" />
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="grid grid-cols-3 md:grid-cols-12 gap-x-4 px-6 py-3.5 border-b border-gray-100 bg-gray-50 text-xs font-semibold text-[#1B365D]">
          <div className="col-span-1 md:col-span-5">Nome Banca</div>
          <div className="col-span-1 md:col-span-5">Data creazione</div>
          <div className="hidden md:block md:col-span-2" />
        </div>

        <div className="divide-y divide-gray-100">
          {banks.length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-gray-400">Nessuna banca trovata.</div>
          ) : (
            banks.map((bank) => (
              <div
                key={bank.id}
                className="grid grid-cols-3 md:grid-cols-12 gap-x-4 px-6 py-4 hover:bg-gray-50/50 transition duration-150 items-center"
              >
                <div className="col-span-1 md:col-span-5 text-xs font-medium text-gray-800">{bank.name}</div>
                <div className="col-span-1 md:col-span-5 text-xs text-gray-500">
                  {new Date(bank.created_at).toLocaleDateString('it-IT')}
                </div>
                <div className="col-span-1 md:col-span-2 flex justify-end">
                  <Link
                    to={routesMap.VISUALIZZA_BANCA(bank.name)}
                    state={{ id: bank.id }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#1B365D] text-white hover:bg-[#142a4a] transition-colors"
                  >
                    Visualizza
                    <EyeIcon />
                  </Link>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ContainerBanksPage;
